import { Modal, useModals } from 'almanac-ui/Modal'
import Styles from './ContentTypeSelectorWithCounts.module.scss'
import { ContentCategoryCounter } from './ContentCategoryCounter'
import { useMemo } from 'react'
import {
  CONTENT_CATEGORIES,
  CONTENT_CATEGORY_TO_CREDIT_KEY,
  ContentCategory,
  ContentTypeCounts,
  getContentCategoryForScheduleContent,
} from 'utils/brandPlans/contentCategories'
import { useRootStore } from 'stores/RootStore'
import { Text } from 'blaze-ui/Text/Text'
import { useBrandPlanSchedule } from 'hooks/queries/schedules/useBrandPlanSchedule'
import { BrandPlanSchedule } from 'types'
import { CreditUsageButton } from 'blaze-ui/CreditUsageButton/CreditUsageButton'

interface ContentTypeSelectorWithCountsProps {
  scheduleId: number
  onNext: (schedule?: BrandPlanSchedule) => void
  counts: ContentTypeCounts
  setCounts: React.Dispatch<React.SetStateAction<ContentTypeCounts>>
}

export const ContentTypeSelectorWithCounts = ({
  onNext,
  scheduleId,
  counts,
  setCounts,
}: ContentTypeSelectorWithCountsProps) => {
  const { closeModal } = useModals()
  const { creditUsageStore } = useRootStore()
  const { data: schedule } = useBrandPlanSchedule(scheduleId)

  const usageCosts = creditUsageStore.creditUsage?.usage_costs

  const totalCredits = useMemo(
    () =>
      CONTENT_CATEGORIES.reduce((sum, category) => {
        const perPost = usageCosts?.[CONTENT_CATEGORY_TO_CREDIT_KEY[category]] ?? 0
        return sum + counts[category] * perPost
      }, 0),
    [counts, usageCosts],
  )

  const totalNewPosts = useMemo(
    () =>
      CONTENT_CATEGORIES.reduce((sum, category) => {
        return sum + counts[category]
      }, 0),
    [counts],
  )

  const hasSelection = Object.values(counts).some((count) => count > 0)

  const existingCountByCategory = useMemo(() => {
    const tallies = Object.fromEntries(CONTENT_CATEGORIES.map((c) => [c, 0])) as Record<ContentCategory, number>
    for (const item of schedule?.contents ?? []) {
      const cat = getContentCategoryForScheduleContent(item)
      if (cat) tallies[cat] += 1
    }
    return tallies
  }, [schedule?.contents])

  return (
    <>
      <Modal.Header title="Choose how many new posts you would like" />
      <Modal.Content>
        <div className={Styles.body}>
          <div className={Styles.grid}>
            {CONTENT_CATEGORIES.map((category) => (
              <ContentCategoryCounter
                key={category}
                category={category}
                count={counts[category]}
                currentCount={existingCountByCategory[category]}
                onCountChange={(category, count) =>
                  setCounts((prev) => ({
                    ...prev,
                    [category]: Math.max(0, count),
                  }))
                }
              />
            ))}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton size="lg" variant="tertiary" onPress={closeModal}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <div className={Styles.footerRight}>
            {hasSelection && (
              <Text variant="label" color="var(--dark-40)">
                {totalNewPosts} new posts
              </Text>
            )}
            <CreditUsageButton
              color="var(--light-60)"
              credits={totalCredits}
              onPress={() => onNext(schedule)}
              isDisabled={!hasSelection}
              size="lg"
            >
              Confirm
            </CreditUsageButton>
          </div>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
